import axios from "axios";
import dayjs from "dayjs";
import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { usePortal } from "../../../context/PortalsContext";
import { useToast } from "../../../context/ToastContext";
import {
  createUpdateUserImportJob,
  downloadUserImportJobValidationLog,
  executeUserImportJob,
  getS3PresignedUrl,
  getTemplate,
  validateUserImportJob,
} from "../../../services/api/apiClient";
import { PresignFileType } from "../../../services/api/types/portalConfiguration/uploadPhoto";
import {
  UserImportJobExecuteQueryParams,
  UserImportJobExecuteResult,
  UserImportJobStatus,
  UserImportJobValidateResult,
} from "../../../services/api/types/users/userActions";
import { buildCsv, downloadCsv } from "../../../utils/csv";
import { extractErrorMessage } from "../../../utils/errorUtils";

export type SectionType = "new" | "updates" | "archive" | "reactivate";

const TRANSLITERATIONS: Record<string, string> = {
  Æ: "AE",
  æ: "ae",
  Ø: "O",
  ø: "o",
  Œ: "OE",
  œ: "oe",
  ß: "ss",
  Đ: "D",
  đ: "d",
  Ł: "L",
  ł: "l",
  Þ: "Th",
  þ: "th",
};

const buildTemplateFileName = (prefix: string, portalName?: string): string => {
  const portalSegment = (portalName ?? "")
    .replace(/[ÆæØøŒœßĐđŁłÞþ]/g, (c) => TRANSLITERATIONS[c])
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^A-Za-z0-9_-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-|-$/g, "");
  return [prefix, portalSegment, dayjs().format("YYYY-MM-DD-HHmmss")]
    .filter(Boolean)
    .join("-");
};

export interface UseBulkUserImportReturn {
  // navigation
  activeStep: number;
  goToStep: (step: number) => void;
  reset: () => void;

  // job + file
  jobId: string | null;
  file: File | null;
  setFile: (file: File | null) => void;
  hasRunValidation: boolean;

  // step 1
  downloadTemplate: (prefilled: boolean) => Promise<void>;
  downloadingTemplate: "blank" | "prefilled" | null;
  uploadFile: (file: File) => Promise<{ jobId: string; path: string } | null>;
  runValidation: () => Promise<void>;
  isUploading: boolean;
  // A new file has been uploaded but not yet validated — the "Run validation"
  // button is shown while this is true.
  awaitingValidation: boolean;

  // step 2
  validation: UserImportJobValidateResult | null;
  isValidating: boolean;
  autoFix: boolean;
  setAutoFix: (value: boolean) => void;
  downloadValidationLog: () => Promise<void>;

  // step 3
  includedSections: Record<SectionType, boolean>;
  toggleSection: (section: SectionType, value: boolean) => void;
  execute: () => Promise<void>;
  isExecuting: boolean;

  // step 4
  executeResult: UserImportJobExecuteResult | null;
}

export const useBulkUserImport = (): UseBulkUserImportReturn => {
  const { portalUid } = useParams<{ portalUid: string }>();
  const { portals } = usePortal();
  const { showToast } = useToast();

  const [activeStep, setActiveStep] = useState(0);
  const [jobId, setJobId] = useState<string | null>(null);
  const [uploadedPath, setUploadedPath] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [hasRunValidation, setHasRunValidation] = useState(false);

  const [downloadingTemplate, setDownloadingTemplate] = useState<
    "blank" | "prefilled" | null
  >(null);
  const [isUploading, setIsUploading] = useState(false);
  const [awaitingValidation, setAwaitingValidation] = useState(false);

  const [validation, setValidation] =
    useState<UserImportJobValidateResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [autoFix, setAutoFix] = useState(true);

  const [includedSections, setIncludedSections] = useState<
    Record<SectionType, boolean>
  >({ new: true, updates: true, archive: false, reactivate: true });
  const [isExecuting, setIsExecuting] = useState(false);
  const [executeResult, setExecuteResult] =
    useState<UserImportJobExecuteResult | null>(null);

  const goToStep = useCallback((step: number) => setActiveStep(step), []);

  const downloadTemplate = useCallback(
    async (prefilled: boolean) => {
      if (!portalUid) return;
      setDownloadingTemplate(prefilled ? "prefilled" : "blank");
      try {
        const result = await getTemplate(portalUid, { isPrefilled: prefilled });
        const headers = result.columns.map((c) => c.key);
        const csv = buildCsv(headers, result.rows ?? []);
        const portalName = portals.portals.find(
          (p) => p.portalUid === portalUid,
        )?.name;
        downloadCsv(
          buildTemplateFileName(
            prefilled ? "user-import-prefilled" : "user-import-template",
            portalName,
          ),
          csv,
        );
      } catch (error) {
        showToast(
          extractErrorMessage(error, "Failed to download template"),
          "error",
        );
      } finally {
        setDownloadingTemplate(null);
      }
    },
    [portalUid, portals.portals, showToast],
  );

  const uploadFile = useCallback(
    async (selected: File) => {
      if (!portalUid) return null;

      setActiveStep(0);
      setFile(selected);
      setIsUploading(true);
      setAwaitingValidation(true);
      setValidation(null);
      setJobId(null);
      setUploadedPath(null);
      try {
        const { UserImportJobUid } = await createUpdateUserImportJob(
          portalUid,
          {
            filePath: "",
            autoFix,
            status: UserImportJobStatus.UPLOADED,
          },
        );

        const contentType = selected.type || "text/csv";
        const presigned = await getS3PresignedUrl(portalUid, {
          fileName: selected.name,
          contentType,
          fileType: PresignFileType.USER_IMPORT_JOB,
          jobId: UserImportJobUid,
        });
        await axios.put(presigned.uploadUrl, selected, {
          headers: { "Content-Type": contentType },
        });

        setJobId(UserImportJobUid);
        setUploadedPath(presigned.path);
        return { jobId: UserImportJobUid, path: presigned.path };
      } catch (error) {
        setAwaitingValidation(false);
        showToast(extractErrorMessage(error, "Failed to upload file"), "error");
        return null;
      } finally {
        setIsUploading(false);
      }
    },
    [portalUid, autoFix, showToast],
  );

  const validateJob = useCallback(
    async (activeJobId: string, activePath: string) => {
      if (!portalUid) return;
      setIsValidating(true);
      setValidation(null);
      try {
        await createUpdateUserImportJob(portalUid, {
          UserImportJobUid: activeJobId,
          filePath: activePath,
          autoFix,
          status: UserImportJobStatus.UPLOADED,
        });

        const result = await validateUserImportJob(portalUid, activeJobId);
        setValidation(result);
        setHasRunValidation(true);
        setAwaitingValidation(false);
        setActiveStep(1);
      } catch (error) {
        showToast(
          extractErrorMessage(error, "Failed to validate file"),
          "error",
        );
      } finally {
        setIsValidating(false);
      }
    },
    [portalUid, autoFix, showToast],
  );

  const runValidation = useCallback(async () => {
    if (!jobId || !uploadedPath) return;
    await validateJob(jobId, uploadedPath);
  }, [jobId, uploadedPath, validateJob]);

  const handleSetAutoFix = useCallback(
    (value: boolean) => {
      setAutoFix(value);
      if (!portalUid || !jobId) return;

      createUpdateUserImportJob(portalUid, {
        UserImportJobUid: jobId,
        filePath: uploadedPath ?? "",
        autoFix: value,
        status: UserImportJobStatus.UPLOADED,
      }).catch((error) => {
        setAutoFix(!value);
        showToast(
          extractErrorMessage(error, "Failed to update auto-fix setting"),
          "error",
        );
      });
    },
    [portalUid, jobId, uploadedPath, showToast],
  );

  const downloadValidationLog = useCallback(async () => {
    if (!portalUid || !jobId) return;
    try {
      const items = await downloadUserImportJobValidationLog(portalUid, jobId);
      const headers = [
        "FailurePhase",
        "FailureType",
        "Message",
        "Email",
        "Name",
      ];
      const csv = buildCsv(
        headers,
        items as unknown as Record<string, unknown>[],
      );
      downloadCsv("validation-log", csv);
    } catch (error) {
      showToast(
        extractErrorMessage(error, "Failed to download validation log"),
        "error",
      );
    }
  }, [portalUid, jobId, showToast]);

  const toggleSection = useCallback((section: SectionType, value: boolean) => {
    setIncludedSections((prev) => ({ ...prev, [section]: value }));
  }, []);

  const execute = useCallback(async () => {
    if (!portalUid || !jobId) return;
    const params: UserImportJobExecuteQueryParams = {};
    if (includedSections.new) params.new = true;
    if (includedSections.updates) params.updates = true;
    if (includedSections.archive) params.archive = true;
    if (includedSections.reactivate) params.reactivate = true;

    setIsExecuting(true);
    try {
      const result = await executeUserImportJob(portalUid, jobId, params);
      setExecuteResult(result);
      setActiveStep(3);
    } catch (error) {
      showToast(
        extractErrorMessage(error, "Failed to execute bulk action"),
        "error",
      );
    } finally {
      setIsExecuting(false);
    }
  }, [portalUid, jobId, includedSections, showToast]);

  // Restore the whole wizard to its initial state (used when the user
  // re-enters the page from the sidebar).
  const reset = useCallback(() => {
    setActiveStep(0);
    setJobId(null);
    setUploadedPath(null);
    setFile(null);
    setHasRunValidation(false);
    setDownloadingTemplate(null);
    setIsUploading(false);
    setAwaitingValidation(false);
    setValidation(null);
    setIsValidating(false);
    setAutoFix(true);
    setIncludedSections({
      new: true,
      updates: true,
      archive: false,
      reactivate: true,
    });
    setIsExecuting(false);
    setExecuteResult(null);
  }, []);

  return {
    activeStep,
    goToStep,
    reset,
    jobId,
    file,
    setFile,
    hasRunValidation,
    downloadTemplate,
    downloadingTemplate,
    uploadFile,
    runValidation,
    isUploading,
    awaitingValidation,
    validation,
    isValidating,
    autoFix,
    setAutoFix: handleSetAutoFix,
    downloadValidationLog,
    includedSections,
    toggleSection,
    execute,
    isExecuting,
    executeResult,
  };
};
